/* iter-222 · OperationalNotesPanel
 *
 * Calm operational memory panel for the Deal Room right rail.
 * NOT a generic notes widget. NOT a task manager. NOT a CRM dump.
 *
 * Each note is critical transaction context everyone on the deal
 * should know — pinned for emphasis, resolved when no longer
 * relevant, lightweight inline edit for typos.
 *
 * Lean pilot surface: add · pin · resolve.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Pin, PinOff, Check, Plus, Loader2, Trash2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return "just now";
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch { return ""; }
}

// `embedded`: rendered inside the Deal Room notes bar — no card chrome / header / duplicate Add actions.
export function OperationalNotesPanel({ dealId, canEdit = true, onChange, embedded = false, startComposing = false, onSaved }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(startComposing && canEdit);
  const [composeBody, setComposeBody] = useState("");
  const [pendingPin, setPendingPin] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/v2/deals/${dealId}/operational-notes`);
      setNotes(Array.isArray(data) ? data : []);
    } catch (err) {
      // Calm — never noisy on rail panels.
      if (err?.response?.status !== 401 && err?.response?.status !== 404) {
        toast.error("Couldn't load notes", { description: apiError(err) });
      }
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const { pinned, active, resolved } = useMemo(() => {
    const p = [], a = [], r = [];
    (notes || []).forEach((n) => {
      if (n.resolved) r.push(n);
      else if (n.pinned) p.push(n);
      else a.push(n);
    });
    return { pinned: p, active: a, resolved: r };
  }, [notes]);

  const submitNote = useCallback(async () => {
    const body = composeBody.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      await api.post(`/api/v2/deals/${dealId}/operational-notes`, {
        body, pinned: pendingPin,
      });
      setComposeBody("");
      setPendingPin(false);
      setComposing(false);
      await fetchNotes();
      onChange?.();
      onSaved?.();
      toast.success("Note added");
    } catch (err) {
      toast.error("Couldn't add note", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  }, [composeBody, pendingPin, busy, dealId, fetchNotes, onChange]);

  const togglePin = useCallback(async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/api/v2/deals/${dealId}/operational-notes/${n.id}`, {
        pinned: !n.pinned,
      });
      await fetchNotes();
      onChange?.();
    } catch (err) {
      toast.error("Couldn't update pin", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  }, [busy, dealId, fetchNotes, onChange]);

  const toggleResolve = useCallback(async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/api/v2/deals/${dealId}/operational-notes/${n.id}`, {
        resolved: !n.resolved,
      });
      await fetchNotes();
      onChange?.();
    } catch (err) {
      toast.error("Couldn't update note", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  }, [busy, dealId, fetchNotes, onChange]);

  const removeNote = useCallback(async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/api/v2/deals/${dealId}/operational-notes/${n.id}`);
      await fetchNotes();
      onChange?.();
    } catch (err) {
      toast.error("Couldn't delete note", { description: apiError(err) });
    } finally {
      setBusy(false);
    }
  }, [busy, dealId, fetchNotes, onChange]);

  const totalVisible = pinned.length + active.length + resolved.length;

  return (
    <section
      className={embedded ? "" : "rounded-2xl bg-white p-5 cf-card-hover"}
      style={embedded ? undefined : {
        border: "1px solid rgba(226,232,240,0.9)",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
      }}
      data-testid="canonical-rail-operational-notes"
    >
      {!embedded && <header className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[11px] font-bold uppercase tracking-[0.10em] text-slate-500">
            Operational notes
          </h3>
          <p className="text-[10.5px] text-slate-400 mt-0.5">
            Context everyone on the deal should know
          </p>
        </div>
        {canEdit && !composing && (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-violet-700 hover:text-violet-900 hover:bg-violet-50"
            data-testid="operational-notes-add-trigger"
          >
            <Plus size={11} strokeWidth={2.4} />
            Add
          </button>
        )}
      </header>}

      {composing && (
        <div
          className={`rounded-lg ring-1 ring-violet-200/70 bg-violet-50/30 ${embedded ? "px-3 py-2 mb-2" : "px-3 py-3 mb-3"}`}
          data-testid="operational-notes-composer"
        >
          <textarea
            value={composeBody}
            onChange={(e) => setComposeBody(e.target.value)}
            placeholder="e.g. Buyer pushing for 28-day exchange"
            rows={2}
            maxLength={500}
            className="w-full bg-transparent text-[12.5px] text-slate-900 placeholder:text-slate-400 resize-none outline-none"
            data-testid="operational-notes-composer-input"
            autoFocus
          />
          <div className="flex items-center justify-between mt-2 gap-2">
            <button
              type="button"
              onClick={() => setPendingPin((v) => !v)}
              className={`inline-flex items-center gap-1 h-7 px-2 rounded-md text-[10.5px] font-semibold transition ${
                pendingPin
                  ? "bg-violet-100 text-violet-700 ring-1 ring-violet-200"
                  : "text-slate-500 hover:bg-slate-100 ring-1 ring-transparent"
              }`}
              data-testid="operational-notes-composer-pin"
            >
              <Pin size={10} strokeWidth={2.4} />
              {pendingPin ? "Pinned" : "Pin"}
            </button>
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                type="button"
                onClick={() => { setComposing(false); setComposeBody(""); setPendingPin(false); }}
                disabled={busy}
                className="h-7 px-2.5 rounded-md text-[11px] font-semibold text-slate-500 hover:text-slate-700 hover:bg-white"
                data-testid="operational-notes-composer-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitNote}
                disabled={busy || !composeBody.trim()}
                className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="operational-notes-composer-submit"
              >
                {busy && <Loader2 size={10} className="animate-spin" />}
                Add note
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && totalVisible === 0 && (
        <p className="text-[11px] text-slate-400" data-testid="operational-notes-loading">
          Loading notes…
        </p>
      )}

      {!loading && totalVisible === 0 && !composing && (
        /* iter-242 — Empty state gets a one-tap "Add note" action
            so the operator's next step is obvious. */
        <div className="text-center py-1" data-testid="operational-notes-empty">
          <p className="text-[11.5px] text-slate-500">No operational notes yet</p>
          {canEdit && !embedded && (
            <button
              type="button"
              onClick={() => setComposing(true)}
              className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-violet-700 hover:text-violet-900"
              data-testid="operational-notes-empty-add"
            >
              <Plus size={11} strokeWidth={2.4} />
              Add note
            </button>
          )}
        </div>
      )}

      {totalVisible > 0 && (
        <ul className={embedded ? "divide-y divide-slate-100" : "space-y-2.5"} data-testid="operational-notes-list">
          {pinned.map((n) => (
            <NoteRow
              key={n.id}
              n={n}
              tone="pinned"
              canEdit={canEdit}
              onTogglePin={() => togglePin(n)}
              onToggleResolve={() => toggleResolve(n)}
              onRemove={() => removeNote(n)}
              embedded={embedded}
            />
          ))}
          {active.map((n) => (
            <NoteRow
              key={n.id}
              n={n}
              tone="active"
              canEdit={canEdit}
              onTogglePin={() => togglePin(n)}
              onToggleResolve={() => toggleResolve(n)}
              onRemove={() => removeNote(n)}
              embedded={embedded}
            />
          ))}
          {resolved.map((n) => (
            <NoteRow
              key={n.id}
              n={n}
              tone="resolved"
              canEdit={canEdit}
              onTogglePin={() => togglePin(n)}
              onToggleResolve={() => toggleResolve(n)}
              onRemove={() => removeNote(n)}
              embedded={embedded}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function NoteRow({ n, tone, canEdit, onTogglePin, onToggleResolve, onRemove, embedded = false }) {
  const isResolved = tone === "resolved";
  const isPinned = tone === "pinned";
  return (
    <li
      className={`group relative transition ${embedded ? "px-2 py-2" : "rounded-lg px-3 py-2.5"} ${
        embedded
          ? (isPinned ? "bg-amber-50/40" : "hover:bg-slate-50/70")
          : isPinned
          ? "bg-amber-50/60 ring-1 ring-amber-200/70"
          : isResolved
            ? "bg-slate-50/60"
            : "bg-slate-50/50 hover:bg-slate-100/60"
      }`}
      data-testid={`operational-note-${n.id}`}
    >
      <div className="flex items-start gap-2">
        {isPinned && (
          <Pin size={11} className="text-amber-600 mt-[3px] shrink-0" strokeWidth={2.4} aria-label="Pinned" />
        )}
        <div className="min-w-0 flex-1">
          <p
            className={`text-[12.5px] leading-snug ${
              isResolved ? "text-slate-500 line-through decoration-slate-300" : "text-slate-900"
            }`}
            data-testid={`operational-note-${n.id}-body`}
          >
            {n.body}
          </p>
          <p className="text-[10px] text-slate-400 mt-1">
            {n.created_by_name || "Agent"} · {relTime(n.created_at)}
            {n.resolved && n.resolved_at && (
              <> · Resolved {relTime(n.resolved_at)}</>
            )}
          </p>
        </div>
        {canEdit && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0">
            <button
              type="button"
              onClick={onTogglePin}
              className="h-6 w-6 rounded text-slate-400 hover:text-amber-600 hover:bg-white inline-flex items-center justify-center"
              title={isPinned ? "Unpin" : "Pin"}
              aria-label={isPinned ? "Unpin note" : "Pin note"}
              data-testid={`operational-note-${n.id}-pin`}
            >
              {isPinned ? <PinOff size={11} /> : <Pin size={11} />}
            </button>
            <button
              type="button"
              onClick={onToggleResolve}
              className={`h-6 w-6 rounded inline-flex items-center justify-center hover:bg-white ${
                isResolved ? "text-emerald-600" : "text-slate-400 hover:text-emerald-600"
              }`}
              title={isResolved ? "Re-open" : "Mark resolved"}
              aria-label={isResolved ? "Re-open note" : "Mark note resolved"}
              data-testid={`operational-note-${n.id}-resolve`}
            >
              <Check size={11} strokeWidth={2.4} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="h-6 w-6 rounded text-slate-400 hover:text-rose-600 hover:bg-white inline-flex items-center justify-center"
              title="Delete"
              aria-label="Delete note"
              data-testid={`operational-note-${n.id}-delete`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}

export default OperationalNotesPanel;
